/* eslint-disable no-prototype-builtins */
import Slot from '../slot';
import {
  arraysMatch,
  performanceMeasure,
  performanceMark,
  distanceFromBottom,
  closestTo,
  devtools,
  distanceOfElementBottomFromViewportBottom,
} from '../utils';
import Ad from '../ad';
import Bid from '../bid';
import Constants from '../constants';
import Logger from '../logger';
import Metrics from '../metrics';
import Events, { EventTypes } from '../events';
import { getRefreshRate, isRefreshDisabled } from '../refresh_rate';
import { getClosestBucketForValue } from '../../buckets';
import store from '../store';
import { scrollVelocityExceedsThreshold } from '../../plugins/scroll_velocity';
import GamAdSlotWrapper from '../slot_wrappers/gam_ad_slot_wrapper';

/**
 * Optional string flag read from app.settings.refreshSizeBehavior controlling how a slot's size
 * list is resolved when it refreshes. When unset (or any other value), the existing behavior runs.
 * See sizesForRefresh().
 */
export const REFRESH_SIZE_BEHAVIOR = {
  // Only kicks in when the refresh would otherwise request an empty list (the previous render was
  // empty / had no measurable iframe): retain the previous request's sizes instead of requesting []
  // and defining a sizeless slot. A measurable render still narrows normally.
  PREVENT_EMPTY: 'prevent-empty',
  // Disable the eligible-size calculation entirely: always request the full configured size list
  // (this.data.sizes), never narrowing to the previously rendered size on refresh.
  ALL_SIZES: 'all-sizes',
};

// Browsi viewability prediction at or above which a slot is tagged `viewable=yes` (Atlas MG
// ViewableTargeting). See browsiPredictsViewable().
export const BROWSI_VIEWABLE_THRESHOLD = 0.4;

// Special, non-fixed sizes. `fluid` (and, in future, placeholder sizes such as [1, 1]) have no
// comparable dimensions, so the threshold-based eligibleSizesForRefresh() always drops them; the
// prevent-empty behavior re-adds any the configured sizes include via preserveSpecialSizes().
const SPECIAL_SIZES = ['fluid'];

export default class GamAdSlot extends Slot {
  constructor(app, data, neighbor) {
    super(app, data, neighbor);

    // We also want to store the ID. Generally, we have just the prefix and the
    // name of the slot, but in case we want to change that, we can.
    this.id = this.data.id ? this.data.id : 'div-gpt-ad-' + this.name;

    // Create a new instance of Ad, which is responsible for displaying creative
    this.ad = new Ad(this);

    // Create a local targeting map for reference
    this.targeting = {};

    /**
     * Placeholder for the slot object returned by DFP eventually
     * @type {object}
     */
    this.slot = null;
  }

  /**
   * We take anything passed on as an extra option, which could include things
   * like this slot is not prebid eligible, and add it into the data we want
   * to store in the recorded slots.
   * @return {object} Official slot object
   */
  applyDefaults(refresh = false) {
    // Capture the sizes actually requested in the previous cycle before super/defaults reset
    // this.sizes back to the configured list. Used by the 'prevent-empty' behavior in
    // sizesForRefresh() to fall back to the last requested sizes instead of requesting [].
    const previousRequestedSizes = Array.isArray(this.sizes) ? this.sizes : [];

    super.applyDefaults({ refresh });

    // Mark keys to exclude from data. `exemptFromBlocking` is excluded because super.applyDefaults()
    // already normalized it (fresh array); re-copying the raw config value here would clobber that.
    const exclude = ['name', 'exemptFromBlocking'];

    // These defaults live directly on the Slot object
    let slotDefaults = {
      sticky: false,
      sizes: [],
      customSizes: [],
      outOfPage: false,
      blockthrough: { dataId: null },
    };

    // We only want to set these if we are not refreshing.
    if (!refresh) {
      slotDefaults.renderedSize = false;
      slotDefaults.listeningForRefresh = false;
    }

    for (var def in slotDefaults) {
      if (slotDefaults.hasOwnProperty(def)) this[def] = slotDefaults[def];
    }

    for (var d in this.data) {
      if (exclude.indexOf(d) > -1) continue;
      if (this.data.hasOwnProperty(d)) this[d] = this.data[d];
    }

    // We add this in after we handle defaults because we want to make sure it
    // always overrides.
    if (refresh) {
      this.sizes = sizesForRefresh.call(this, previousRequestedSizes);
      this.holdSize = this.renderedSize;
    }

    // Now, we add in the attributes that under no circumstances do we want to
    // be able to override as we register this slot.
    const state = {
      prebidEligible: this.data.prebidEligible === false ? false : true,
      // deferDisplay slots are manual one-shots (see Slot#display) — never auto-refresh.
      refreshEligible: this.data.refreshEligible === false || this.data.deferDisplay === true ? false : true,
      enablePermutiveSlotTracking: this.data.permutiveTracking?.enabled === true,
      hasBeenRefreshed: false,
      isBidding: false,
      queuedUntilAfterBid: false,
      // Whether the queued-for-bid render should bypass the display gates — set when the request
      // that queued it was itself forced/out-of-band. See queueUntilAfterBid / bidded.
      queuedForcedRender: false,
      loadTime: false,
      refreshQueued: false,
      viewedSinceRender: false,
      pendingRefresh: false,
    };

    this._state = { ...this._state, ...state };

    autoDisablePrebid.call(this);

    return this;
  }

  /**
   * Whether the optimized refresh path (timer on render, viewability gate) is enabled.
   * Resolved from slot config (optimizeAdRefresh) if set, otherwise from app settings.
   * @returns {boolean}
   */
  isOptimizeAdRefreshEnabled() {
    return (
      (this.data.optimizeAdRefresh !== undefined
        ? this.data.optimizeAdRefresh
        : this.app.settings.optimizeAdRefresh) === true
    );
  }

  // Should the slot be displayed now, or added to the lazy loading observer?
  canDisplayImmediately() {
    return this.isEager() && !this.isPrebidEligible();
  }

  /**
   * Determine whether this slot can be displayed on the page
   * @return {boolean}
   */
  canBeDisplayed() {
    // First things first, if we don't have sizes, we can't do a thing with you.
    if (this.sizes === undefined) {
      Logger.log('No sizes added.');
      return false;
    }

    return super.canBeDisplayed();
  }

  /**
   * Destroy this slot's wrapper and DOM element, and remove it from DFP.
   */
  destroy() {
    // Flag the slot destroyed BEFORE releasing anything: opening a gate resolves the renders held
    // behind it, and those continuations must see this slot is gone (see render()).
    this._state.destroyed = true;

    // Stop auto-refresh before tearing the slot down. queueRefresh() arms this.refreshTimer with a
    // refreshRate delay, and neither isRefreshEligible() nor refresh() checks isDestroyed() — so a
    // timer left armed here fires against an already-destroyed wrapper and re-requests an ad for a
    // div that is no longer on the page, then re-queues itself. Clearing refreshEligible also stops
    // a late GPT callback (rendered/viewed for an in-flight ad) from arming a fresh one. Mirrors
    // step 1 of disableAd(). Matters most on SPA navigation, where uninstall() destroys every slot
    // while its refresh timer is still pending.
    clearTimeout(this.refreshTimer);
    this._state.refreshQueued = false;
    this._state.pendingRefresh = false;
    this._state.refreshEligible = false;

    // Safety net: if the designated first slot is destroyed before it requested or rendered, release
    // both first-slot gates so the page doesn't stall waiting on a slot that no longer exists.
    this.releaseFirstSlotGates();

    super.destroy();

    if (googletag.pubads) googletag.pubads().clear([this.slot]);
  }

  /**
   * Method that runs once the slot is inserted into the DOM
   * @return {undefined}
   */
  inserted() {
    if (!this.canBeDisplayed()) {
      // The designated first slot can't be displayed and will never request; release the firstSlot
      // gate so held slots don't stall waiting on it (they'd otherwise fall back to the backstop).
      // Likewise the firstSlotResponse gate: no response is coming from a slot that never requests.
      this.releaseFirstSlotGates();
      return;
    }

    // Adhesion slots render into a dedicated, controller-created sticky bar
    // rather than their page-emitted div. The adhesion plugin (loaded lazily,
    // when enabled) binds the slot into that bar off this `slotInserted` event —
    // synchronously here, before publishToDfp below, when the chunk is already
    // loaded, or via its catch-up scan when it loads later. The payload carries
    // `slotId` + the `adhesion` flag so the controller can resolve this slot
    // without a name scan.
    Events.emit(EventTypes.slotInserted, {
      slotId: this.id,
      slotName: this.name,
      adhesion: this.adhesion === true,
      enablePermutiveSlotTracking: this.isPermutiveTrackingEnabled(),
    });

    const elem = this.getElement();

    if (elem) {
      elem.dispatchEvent(
        new CustomEvent('concertAdRegistered', { detail: { slotElement: elem }, bubbles: true, composed: true })
      );
    }

    // Publish it to DFP
    publishToDfp.call(this);

    // Create a new Bid for this slot
    if (!this.bid) {
      this.bid = new Bid(this);
    }

    // deferDisplay: park until display(). Defined in GAM above; undo setDefined()'s displayEligible
    // so nothing requests it until the explicit call. See Slot#isDeferDisplay / Slot#display.
    if (this.isDeferDisplay()) {
      this._state.displayEligible = false;
      Logger.log(`${this.name} deferred (deferDisplay); awaiting display()`);
      return;
    }

    this.canDisplayImmediately() ? this.render() : this.observe();
  }

  /**
   * Callback run when slot is sent away for Prebid
   */
  bidding() {
    this._state.isBidding = true;
    Events.emit(EventTypes.bidSent, { slotName: this.name });
  }

  /**
   * Callback run when slot is finished header bidding
   */
  bidded() {
    this._state.isBidding = false;
    Events.emit(EventTypes.bidComplete, { slotName: this.name });

    // Show the slot now that prebid has finished: it either queued here explicitly
    // (requestOrQueueForBid) or is eager-loaded. A queued slot renders with the force it was queued
    // with, so an out-of-band request that deferred for bids still bypasses the display gates it was
    // promised — an eager slot stays unforced and waits on its gates as usual.
    if (this.isQueuedUntilAfterBid()) {
      this.render(this._state.queuedForcedRender === true);
    } else if (this.isEager()) {
      this.render();
    }
  }

  /**
   * Callback run when DFP considers the slot to have contained a viewable impression.
   * @return {undefined}
   */
  viewed() {
    Events.emit(EventTypes.adViewed, { slotName: this.name });
    if (this.isOptimizeAdRefreshEnabled()) {
      this._state.viewedSinceRender = true;
      Logger.log(`[Refresh] ${this.name} viewable (impressionViewable); viewedSinceRender set`);
      if (this._state.pendingRefresh) {
        this._state.pendingRefresh = false;
        Logger.log(`[Refresh] ${this.name} viewable; triggering pending refresh`);
        this.refresh();
      }
    } else {
      Logger.log(`[Refresh] ${this.name} viewed (non-optimized path); queueing refresh`);
      queueRefresh.call(this);
    }
  }

  queueRefreshImmediately() {
    this._state.refreshQueued = false;
    clearTimeout(this.refreshTimer);
    queueRefresh.call(this, 0);
  }

  /**
   * Suppress this slot in response to app.disableAds(): stop it refreshing and blank any live
   * creative, while leaving the slot and its DOM in place. The slot-level counterpart to the
   * app-level disableAds(). Two steps:
   *   1. Cancel any queued/pending auto-refresh and mark the slot refresh-ineligible, so
   *      queueRefresh() short-circuits and a pending refresh timer can't fire. refresh() destroys
   *      the GAM slot before re-requesting and that re-request is blocked by the suppression gate,
   *      so a timer left running would tear the creative down and never replace it.
   *   2. Hold the current rendered height, then clear the creative via GPT pubads().clear(), so a
   *      live ad is emptied out rather than left on the page.
   *
   * Holding the height matters for CLS: clearing empties the ad iframe, and a slot with no configured
   * hold size would otherwise collapse to nothing and shift the page. We reserve the height the same
   * way refresh() does (wrapper.preserveHeight() locks the measured iframe height); a slot that
   * already has a hold size — or was already refreshed — keeps its reserved height and this is a no-op.
   *
   * This does NOT destroy the slot or remove its wrapper/div (unlike destroy()) — the container
   * stays on the page, just blank; the request-time gate (canBeDisplayed/render) keeps it from
   * re-requesting. The clear step is a no-op before the slot has been published to GAM (this.slot is
   * null) or when GPT is not present (e.g. tests).
   * @return {undefined}
   */
  disableAd() {
    // 1. Stop auto-refresh and keep it from re-arming.
    clearTimeout(this.refreshTimer);
    this._state.refreshQueued = false;
    this._state.pendingRefresh = false;
    this._state.refreshEligible = false;

    // 2. Hold the rendered height (no CLS), then blank the live creative — slot/DOM stay in place.
    if (this.slot && googletag.pubads) {
      this.wrapper?.preserveHeight();
      googletag.pubads().clear([this.slot]);
    }
  }

  /**
   * Callback run when the slot is starting to be collapsed
   * @return {undefined}
   */
  collapsed() {
    super.collapsed();

    this._state.refreshEligible = false;
  }

  /**
   * Callback run when the observer marks the slot as visible.
   * @return {undefined}
   */
  observed() {
    Events.emit(EventTypes.slotObserved, { slotName: this.name });

    if (scrollVelocityExceedsThreshold()) {
      const scrollVelocity = store.get('scroll-velocity');

      Logger.log(`${this.name} observed, but scroll velocity ${scrollVelocity} exceeds threshold.`);
      Events.once(EventTypes.scrollVelocityBelowThreshold, () => this.observe());
      return;
    }

    // If a bid is in flight, requestOrQueueForBid() queues the slot for bidded() and returns false;
    // only clear displayEligible once it has actually requested.
    if (this.requestOrQueueForBid()) {
      Logger.log(`${this.name} scrolling into view and displaying`);
      this._state.displayEligible = false;
    }
  }

  /**
   * Request the ad now, unless header bidding is still in flight — in which case defer to bidded(),
   * which renders the slot with Prebid targeting once bids return. Rendering mid-bid would fire the
   * request without those bids. Overrides {@link Slot#requestOrQueueForBid} to add the bid check.
   *
   * An observer-driven request (observed(), force=false) defers on isBidding() alone: it fires
   * because an observer triggered, so the slot is being approached and bidded() is guaranteed to come.
   *
   * A forced, out-of-band request (force=true — the OOP sweep, display()) renders a slot the
   * observer will not reach, so bidded() is not guaranteed. There, deferring is only safe when the
   * auction will complete on its own: with lazy bidding (shouldLazyBid) a watcher-eligible slot's bid
   * is parked behind the bid manager's own IntersectionObserver and only auctions as the slot nears
   * the viewport — for an invisible out-of-page div that observer never fires, so bidded() never fires
   * and waiting on it would hang the slot. isBidInFlightFor() distinguishes an enqueued/in-flight bid
   * from a parked one.
   *
   * What to do about a parked bid depends on the caller, hence awaitParkedBid. The OOP sweep leaves
   * it (false): a skin is force-requested for a head start over the pack, and it cannot wait. An
   * explicit display() passes true: it is a well-timed trigger, so the parked bid is promoted into
   * the auction (promoteParkedBid) and the slot waits for it — otherwise a `deferDisplay` interstitial
   * behind an overlay, whose element never intersects, would always fire without Prebid targeting.
   * Promoting makes the wait safe: an enqueued auction is bounded by prebidTimeout.
   *
   * @param  {boolean} [force=false]  Bypass the display-gate wait, and require an in-flight bid to defer.
   * @param  {boolean} [awaitParkedBid=false]  When forcing, auction a still-parked bid and wait for it
   *   instead of requesting unbidded.
   * @return {boolean}                true if the ad was requested now; false if queued until bidding.
   */
  requestOrQueueForBid(force = false, awaitParkedBid = false) {
    const { bidManager } = this.app;

    // Should we wait for bidded() rather than render now? An observer-driven request always should —
    // the observer fired, so bidded() is guaranteed. A forced request is out-of-band (no observer to
    // complete a parked bid), so only when the auction is genuinely in flight — or when we can put it
    // in flight ourselves and the caller is willing to wait.
    let shouldWaitForBid = true;

    if (force) {
      shouldWaitForBid = !!bidManager && bidManager.isBidInFlightFor(this);

      if (!shouldWaitForBid && awaitParkedBid && bidManager && this.isBidding()) {
        shouldWaitForBid = bidManager.promoteParkedBid(this);
      }
    }

    if (this.isBidding() && shouldWaitForBid) {
      // Carry `force` through: the render that fires from bidded() must keep this request's gate
      // bypass, or a forced slot would silently queue behind the gates it was promised to skip.
      this.queueUntilAfterBid(force);
      return false;
    }
    this.render(force);
    return true;
  }

  /**
   * Callback run when DFP slot is loaded
   * @return {undefined}
   */
  loaded() {
    this._state.loadTime = Date.now() - this._state.loadTime;
    Events.emit(EventTypes.adLoaded, { slotName: this.name });

    performanceMark(`${this.name}-end`);
    performanceMeasure(`${this.name}`);

    Logger.log(`Slot ${this.name} loaded in ${this._state.loadTime}ms.`);

    if (!this._state.collapsed && this.renderedSize && !this._state.hasBeenRefreshed) {
      Metrics.track(`${this.getSizeString()}:loaded`, closestTo(this._state.loadTime, Constants.RESPONSE_TIME_BUCKETS));
      Metrics.track(
        `${this.getSizeString()}:loaded-location:`,
        bucketedRenderLocation(distanceFromBottom(this.getElement()))
      );
    }
  }

  /**
   * Callback run when DFP slot is rendered
   * @param {Event}     Event from DFP
   * @return {undefined}
   */
  rendered(evt) {
    this._state.rendered = true;

    // GAM has answered for the designated first slot (slotRenderEnded also fires for an empty
    // response), so open the firstSlotResponse gate for slots waiting on it. Done first so nothing
    // below can throw before the held slots are released; a no-op for any other slot or a repeat render.
    this.releaseFirstSlotResponseHold();

    // Store GAM response for Permutive SlotViewable/SlotClicked only when this slot has permutiveTracking.enabled
    if (this.data?.permutiveTracking?.enabled && evt && typeof evt === 'object') {
      const ri = {};
      if (evt.advertiserId != null) ri.advertiser_id = evt.advertiserId;
      if (evt.campaignId != null) ri.campaign_id = evt.campaignId;
      if (evt.lineItemId != null) ri.line_item_id = evt.lineItemId;
      if (evt.creativeId != null) ri.creative_id = evt.creativeId;
      if (evt.sourceAgnosticCreativeId != null && ri.creative_id == null) ri.creative_id = evt.sourceAgnosticCreativeId;
      if (evt.sourceAgnosticLineItemId != null && ri.line_item_id == null)
        ri.line_item_id = evt.sourceAgnosticLineItemId;
      if (typeof evt.isEmpty === 'boolean') ri.is_empty = evt.isEmpty;
      this._permutiveGamResponse = Object.keys(ri).length ? ri : undefined;
      Logger.log(
        '[Permutive Slot Tracking] GAM slotRenderEnded: slot=' +
          this.name +
          ' isEmpty=' +
          evt.isEmpty +
          ' advertiserId=' +
          (evt.advertiserId ?? 'n/a') +
          ' campaignId=' +
          (evt.campaignId ?? 'n/a') +
          ' lineItemId=' +
          (evt.lineItemId ?? 'n/a') +
          ' creativeId=' +
          (evt.creativeId ?? 'n/a') +
          ' sourceAgnosticCreativeId=' +
          (evt.sourceAgnosticCreativeId ?? 'n/a') +
          ' sourceAgnosticLineItemId=' +
          (evt.sourceAgnosticLineItemId ?? 'n/a') +
          ' _permutiveGamResponseSet=' +
          !!this._permutiveGamResponse
      );
    }
    const elem = this.getElement();
    const renderLocation = distanceFromBottom(elem);
    const renderedElementBottomLocation = distanceOfElementBottomFromViewportBottom(elem);

    // Update the render time
    this._state.renderTime = Date.now() - this._state.renderTime;
    this.timesRendered++;
    this.renderedSize = this.wrapper.getIframeSize();

    Events.emit(EventTypes.adRendered, {
      slotId: this.id,
      slotName: this.name,
      renderLocation,
      gamEvent: evt,
      renderedSize: this.renderedSize,
      timesRendered: this.timesRendered,
      renderedElementTopLocation: renderLocation,
      renderedElementBottomLocation,
      holdSize: this.getHoldSize(),
    });

    // Run callback on Ad object
    this.ad.rendered();

    // Run callback on SlotWrapper object
    this.wrapper.rendered(evt);

    // Record metrics
    if (!this._state.collapsed && this.renderedSize && !this._state.hasBeenRefreshed) {
      Metrics.track(
        `${this.getSizeString()}:rendered`,
        closestTo(this._state.renderTime, Constants.RESPONSE_TIME_BUCKETS)
      );
      Metrics.track(`${this.getSizeString()}:rendered-location`, bucketedRenderLocation(renderLocation));
    }

    elem.dispatchEvent(
      new CustomEvent('concertAdRendered', { detail: { slotElement: elem }, bubbles: true, composed: true })
    );
    Logger.log(`Rendering slot: ${this.name}`);

    // optimizeAdRefresh: queue refresh when creative has rendered (slotRenderEnded). Offset uses lastDisplayTime set in render() at show(), so first refresh at showTime + refreshRate. Viewability gate: only refresh after viewed; viewedSinceRender reset per creative.
    if (this.isOptimizeAdRefreshEnabled()) {
      Logger.log(`[Refresh] ${this.name} rendered (optimized path); queueing refresh`);
      this._state.viewedSinceRender = false;
      queueRefresh.call(this);
    }
  }

  /**
   * Show an ad in this slot
   * @return {undefined}
   */
  /**
   * Request and show the ad.
   *
   * @param {boolean} [force=false] When true, bypass the display gates and request immediately
   *   without waiting on them. Used by the special-slot (out-of-page / adhesion) sweep driven by
   *   finalizeSlots(), and by Slot#display() for a deferDisplay slot. A swept slot that waits on the
   *   firstSlotResponse gate is forced only once that gate opens. Forced requests must NOT re-await
   *   the gates or they would queue behind — and lose their head start over — the general pack.
   *   Defaults to false: a normal render waits on the active gates.
   */
  render(force = false) {
    if (!super.render()) return;

    const waits = [...this.app.beforeAdsRequested];

    // Adhesion slot only: hold this slot's request until the lazily-loaded adhesion plugin chunk
    // has loaded, so the controller has bound this slot into its bar before it renders. Scoped to
    // the adhesion slot (via the keyed dynamicPlugins registry) so no other slot is delayed by the
    // adhesion library. pmcAdBridge is deliberately NOT awaited here — a GAM creative only reaches
    // it after render, so it must not delay any slot's ad request (see concert-ads #498).
    const adhesionLoaded = this.app.dynamicPlugins?.adhesion;
    if (this.isAdhesion() && adhesionLoaded) {
      waits.push(adhesionLoaded);
    }

    // Display gates (GAM only). A slot holds its request until every active gate it is not exempt
    // from has opened — e.g. the `firstSlot` gate (held until the designated first slot has
    // requested) or, for opted-in slots, the `firstSlotResponse` gate (held until GAM has answered it).
    // gatesBlocking() applies each slot's `exemptFromBlocking` list — e.g. the first slot is exempt
    // from both so it does not deadlock on a gate it opens. A forced render (see `force`) bypasses
    // every gate.
    if (!force) {
      const blocking = this.app.gatesBlocking(this);
      if (blocking.length) {
        waits.push(...blocking);
        Logger.log(`[DisplayGate] ${this.name} waiting on ${blocking.length} display gate(s) before requesting`);
      }
    }

    Promise.all(waits).then(() => {
      // Idempotency guard: if the slot was already requested by the time these waits resolve
      // (e.g. the out-of-page sweep forced a fresh render while an earlier gate-held render was still
      // pending), don't fire a second ad request. A refresh resets displayed first, so this does
      // not block refreshes. Likewise skip a slot destroyed while it waited (e.g. uninstall() on an SPA
      // navigation): opening a gate on the way down releases renders held behind it.
      if (this.isDisplayed() || this.isDestroyed()) return;

      // Ad-suppression re-check (app.disableAds). super.render() only gates synchronously at entry,
      // so a disableAds() that lands while we were awaiting the gate/bids/adhesion load would still
      // let this deferred request fire. Re-check here before requesting. Release the first-slot gates
      // first (this slot will never request or render) so any slots queued behind this one aren't
      // stranded — they resolve, hit this same gate, and bail too.
      if (this.app.adsDisabledFor?.(this)) {
        this.releaseFirstSlotGates();
        return;
      }

      // Show the ad.
      this._state.loadTime = Date.now();
      performanceMark(`${this.name}-start`);

      // optimizeAdRefresh: record display time when we request the ad (show), so first refresh lands at showTime + refreshRate.
      if (this.isOptimizeAdRefreshEnabled()) {
        this._state.lastDisplayTime = Date.now();
      }
      this.ad.show();
      // optimizeAdRefresh: timer is queued in rendered() (slotRenderEnded) using lastDisplayTime from above.

      // The designated first slot opens the firstSlot gate for held slots the moment its request
      // fires, and starts GAM's response countdown for the firstSlotResponse gate.
      this.firstSlotRequested();

      if (devtools) {
        devtools.emit('flush');
      }
    });
  }

  /**
   * Refresh an ad slot. Called internally and in tests.
   * @return {undefined}
   */
  refresh() {
    // For Prebid ads the initial rendered size of the iframe is 1x1
    // the ad is then resized to whatever the requested size was (i.e., 300x250)
    // When this happens, we need to update the renderedSize to whatever the actual size
    // of the iframe is to make sure we can request another ad of the same size
    const prebidRenderedSize = [1, 1];
    if (arraysMatch(this.renderedSize, prebidRenderedSize)) {
      this.renderedSize = this.wrapper.getIframeSize();
      Logger.log(
        `${this.name} was initially rendered as a 1x1. We are updating the rendered size to match the iframe size of ${
          this.renderedSize
        }.`
      );
    }

    // if a slot is being refreshed, we recreate the slot limiting it
    // only to the size that it was initially rendered at
    // so that the page does not jump
    Logger.log(
      `${this.name} being destroyed so we can rebuild it to only accept ${this.renderedSize[0]} and ${
        this.renderedSize[1]
      }`
    );

    // Preserve the height so there is no mobile jank
    this.wrapper.preserveHeight();

    // FYI, googletag should definitely be loaded by this point. Just not for tests.
    googletag.cmd.push(() => {
      // We then destroy the slot according to google.
      googletag.destroySlots([this.slot]);
    });

    // Reset the slot state
    this.applyDefaults(true);
    this.bid = null;

    this._state.hasBeenRefreshed = true;
    Events.emit(EventTypes.slotRefreshed, { slotName: this.name });

    // Kick off the inserted callback again
    this.inserted();
  }

  /**
   * Prevents a slot from rendering until prebid has finished.
   *
   * @param {boolean} [force=false] Whether the render that eventually fires from bidded() should
   *   bypass the display gates. Carries the out-of-band intent of the request that queued this slot
   *   through to that later render: a forced request (e.g. `Slot#display()` on a `deferDisplay`
   *   interstitial) is documented to bypass every gate, and deferring for bids must not silently
   *   re-acquire them — a slot is not exempt from a gate just because it waited for an auction.
   */
  queueUntilAfterBid(force = false) {
    this._state.queuedUntilAfterBid = true;
    this._state.queuedForcedRender = force === true;
  }

  isPrebidEligible() {
    return this._state.prebidEligible;
  }

  /**
   * GAM slots participate in the display gates: their render() awaits `beforeAdsRequested`, so they
   * can also await the active gate promises (e.g. firstSlot) and, when designated the first slot,
   * release the firstSlot gate on request.
   *
   * @return {boolean}
   */
  supportsDisplayGates() {
    return true;
  }

  isBidding() {
    return this._state.isBidding;
  }

  isQueuedUntilAfterBid() {
    return this._state.queuedUntilAfterBid;
  }

  isRefreshEligible() {
    return this._state.refreshEligible && !isRefreshDisabled({ settings: this.app.settings });
  }

  /**
   * Set whether this slot is eligible for auto-refresh. Defaults to enabling
   * refresh; pass an explicit `false` to turn auto-refresh off.
   *
   * @param  {boolean}   [eligible=true]  Whether the slot may auto-refresh.
   * @return {undefined}
   */
  setRefreshEligible(eligible) {
    this._state.refreshEligible = eligible !== false;
  }

  /**
   * Set this slot's auto-refresh interval, in seconds.
   *
   * @param  {number}    inSeconds  The refresh interval, in seconds.
   * @return {undefined}
   */
  setRefreshRate(inSeconds) {
    // Note we do not want to modify the this.data.refreshRate, it is a one time temp setting.
    // If the slot is refreshed, it should revert back to the original refresh rate.
    if (inSeconds > 0) {
      this.refreshRate = inSeconds;
    } else {
      delete this.refreshRate; // reset to default
    }
  }

  /**
   * Whether this slot has Permutive tracking enabled via slot config (permutiveTracking.enabled: true).
   * Used by plugins (e.g. Permutive) to attach tracking for eligible slots.
   * @return {boolean}
   */
  isPermutiveTrackingEnabled() {
    return !!this._state.enablePermutiveSlotTracking;
  }

  isRefreshQueued() {
    return this._state.refreshQueued;
  }

  hasBeenRefreshed() {
    return this._state.hasBeenRefreshed;
  }

  /**
   * Shared pre-request targeting gate for GAM and Rubicon Demand Manager. This is the single
   * place request-time key/values (`refresh`, `viewable`) are decided, so both request paths read
   * the same values off the GPT slot: it is called from the GAM request seam (Ad#show) and the
   * Demand Manager bidding seam (RubiconDemandManagerBidding#buildBiddingQueues, which reads the
   * GPT slot into gptSlotObjects).
   *
   * `refresh`: a slot rendering for the 2nd+ time (hasBeenRefreshed()) is tagged with both the
   * Concert value (true) and Atlas MG's value ('y'), so line items keyed on either `refresh=true`
   * or `refresh=y` match (GAM stringifies the array, yielding ["true", "y"]).
   *
   * `viewable`: mirrors two of Atlas MG's ViewableTargeting rules — a slot rendering for the 2nd+
   * time (`isRefresh`), or one Browsi predicts as viewable (`browsiViewability` >= 0.4, see
   * {@link browsiPredictsViewable}), is treated as viewable. An explicit override set at
   * slot-register time (config `targeting: { viewable: 'yes' }`, merged onto the slot at
   * defineSlot) always wins and is never overwritten. Once set, `viewable` stays in the local
   * targeting map, so later renders keep it.
   *
   * Future: when the condition that sets `viewable` is expanded (e.g. an in-view latch), the value
   * set here will need to persist across refreshes. That would mean persisting targeting the way
   * Atlas does — a persistent map behind setTargeting/getTargeting — which we don't have yet.
   * Additional request-time targeting rules can also be added here so both seams stay in sync.
   *
   * @return {undefined}
   */
  applyRequestTargeting() {
    // A refreshed (2nd+) render is tagged for both Concert (true) and Atlas ('y') line items.
    this.setTargeting('refresh', this.hasBeenRefreshed() ? [true, 'y'] : 'n');

    // An override is already on the slot (config or a prior pass) — leave it untouched.
    if (this.getTargeting('viewable') != null) return;

    // Otherwise a refreshed (2nd+) render, or one Browsi predicts as viewable, is viewable.
    if (this.hasBeenRefreshed() || this.browsiPredictsViewable()) {
      this.setTargeting('viewable', 'yes');
    }
  }

  /**
   * Whether Browsi's viewability prediction for this slot meets Atlas MG's threshold
   * (ViewableTargeting `browsiViewability` >= 0.4). Browsi sets the key straight on the GPT slot,
   * so it is read from there as well as from the local targeting map.
   *
   * @return {boolean}
   */
  browsiPredictsViewable() {
    const values = this.getTargeting('browsiViewability') ?? this.slot?.getTargeting?.('browsiViewability') ?? [];

    return [].concat(values).some(value => Number(value) >= BROWSI_VIEWABLE_THRESHOLD);
  }

  shouldTrackRenderedEvent() {
    return true;
  }

  shouldTrackScrollVelocity() {
    return true;
  }

  /**
   * Set a slot as defined within DFP. While this is only called internally in this
   * class, it is also used during tests.
   *
   * This also calls a refresh listener for ads that might want to individually
   * disable refreshing.
   *
   * @return {undefined}
   */
  setDefined() {
    super.setDefined();

    if (!this.listeningForRefreshEligibility) {
      listenForRefreshEligibility.call(this);
    }

    if (!this.listeningForRefreshImmediately) {
      listenForRefreshImmediately.call(this);
    }
  }

  /**
   * Build the full GAM slug based on all arguments
   *
   * @return {String}
   */
  getGamSlug() {
    return this.app.settings.slug;
  }

  /**
   * Returns the name of the rendered size as `width x height` of the slot
   * ie: 300x250, 300x50, ...
   * @return {String} A string representation of the size.
   */
  getSizeString() {
    return this.renderedSize ? this.renderedSize.join('x') : '-';
  }

  /**
   * Mark a slot as prebid ineligible. Called from constructor and Bid.
   * @return {undefined}
   */
  markPrebidIneligible() {
    this._state.prebidEligible = false;
  }

  /**
   * Reset this slot's refresh queued status. Called from Ad.
   * @return {undefined}
   */
  resetRefreshQueued() {
    this._state.refreshQueued = false;
  }

  /**
   * Set targeting on the DFP slot
   * @param {string} key   Targeting key
   * @param {mixed} value  Targeting value
   */
  setTargeting(key, value) {
    this.targeting[key] = value;

    // It's possible that plugins will try to set targeting before the slot has been defined in GAM.
    // publishToDfp() will handle setting any previous targeting on the local object above.
    if (this.slot) {
      this.slot.setTargeting(key, value);
    }
  }

  /**
   * Get a value previously set as targeting using setTargeting()
   * @param {string} key Targeting key
   */
  getTargeting(key) {
    return this.targeting[key];
  }

  /**
   * Get the holdSize for a slot, falling back to preview if given
   * @return {mixed} Array [100,100] or null
   */
  getHoldSize() {
    return super.getHoldSize();
  }

  /**
   * Get slot wrapper
   * @param  {Slot}     slot       Instance of Slot
   * @param  {object}   config     Config
   * @param  {boolean}  existing   Whether we're creating from an existing DOM el
   * @return {GamAdSlotWrapper}         GamAdSlotWrapper instance
   */
  getSlotWrapper({ slot, config, existing }) {
    return new GamAdSlotWrapper({ slot, config, existing });
  }

  /**
   * Determine if a given DOM node is eligible to have a sibling ad rendered
   * @param  {Node} node   Sibling Element (before or after)
   * @return {boolean}
   */
  static canHaveSibling(node, config) {
    if (!node) {
      return true;
    }

    // If previous or next element isn't an ad, it can have a sibling
    if (!/\bm-ad\b/.test(node.className)) {
      return true;
    }

    let slot = node.children ? node.children[0].__slot__ : null;

    // If the slots share the same name, they shouldn't be placed next
    // to one another regardless of the allowSiblings setting
    if (slot && slot.name === config.name) {
      return false;
    }

    // Check if the slot can have a sibling
    if (slot && slot.data.allowSiblings) {
      return true;
    }

    return false;
  }

  static shouldSkip(app, slotConfig, neighbor, settings) {
    let prev;
    let next;
    if (slotConfig.insertion === 'inside') {
      prev = neighbor.lastElementChild;
      next = neighbor.nextElementSibling;
    } else if (slotConfig.insertion === 'after') {
      prev = neighbor;
      next = neighbor.nextElementSibling;
    } else {
      prev = neighbor.previousElementSibling;
      next = neighbor;
    }

    if (!this.canHaveSibling(prev, slotConfig)) {
      return true;
    }

    if (!this.canHaveSibling(next, slotConfig)) {
      return true;
    }

    if (containerLimitMaxedOut.call(app, prev)) {
      Logger.log(`${slotConfig.name} wasn't rendered. The container has enough slots.`);
      return true;
    }

    if (slotConfig.sizes === undefined) {
      Logger.log(`${slotConfig.name} wasn't inserted. Sizes were not provided.`);
      return true;
    }

    slotConfig.sizes = reduceSizesByFilters(slotConfig, neighbor, settings);

    if (!slotConfig.sizes.length) {
      Logger.log(`${slotConfig.name} has no more slot sizes after filters; skipping`);
      return true;
    }

    return false;
  }

  static isSlotTypeFor(config) {
    return !config.type || config.type === 'gam-ad';
  }
}

/**
 * Be able to nip prebid disabling in the bud if we marked the slot as
 * disabled.
 */
function autoDisablePrebid() {
  let prebidSettings = this.app.settings.prebid || {};
  let defaultConfig = prebidSettings.defaultConfig || {};

  if (
    (prebidSettings[this.configName] && prebidSettings[this.configName].disabled) ||
    (defaultConfig[this.configName] && defaultConfig[this.configName].disabled)
  ) {
    this._state.prebidEligible = false;
  }
}

/**
 * Build the GAM path
 *
 * @return {string}
 */
export function getGamSlugPath(slot) {
  let slugPath = slot.app.settings.slugPath || '';
  const forwardSlashString = '^/';
  const startsWithForwardSlashRegExp = new RegExp(forwardSlashString);

  if (slugPath !== '' && !startsWithForwardSlashRegExp.test(slugPath)) {
    slugPath = '/' + slugPath;
  }

  if (slot.app.settings.appendSlotNameToGAMSlug) {
    slugPath = slugPath + '/' + slot.configName;
  }

  if (typeof slot.data.pageNumberPosition === 'object' && slot.data.pageNumberPosition.name) {
    const pageNumberPositionName = slot.data.pageNumberPosition.name;
    const pageNumberPositionIncremented = slot.data.pageNumberPosition.increment;
    slugPath = slugPath + '/' + `${pageNumberPositionName}${pageNumberPositionIncremented ? slot.index + 1 : ''}`;
  }

  return slugPath;
}

/**
 * Publish the slot to DFP
 * @return {undefined}
 */
function publishToDfp() {
  if (this.isDefined()) {
    return false;
  }

  this.setDefined();

  googletag.cmd.push(() => {
    var adSlot;
    var slug = this.getGamSlug();
    var slugPath = getGamSlugPath(this);
    var fullGamSlug = slug + slugPath;
    var sizes = this.sizes;

    if (this.customSizes && this.customSizes.length > 0 && !this._state.hasBeenRefreshed) {
      var customSize = this.customSizes.filter(custom => {
        return this.index === custom.placement;
      });

      if (customSize.length > 0) {
        // make the custom slot filterable
        customSize[0].name = this.name;
        sizes = reduceSizesByFilters(customSize[0], this.element);
        Logger.log(`${this.name} using custom sizing ${sizes}`);
      }
    }

    // Get the slot object, after sending it all the information.
    if (this.outOfPage) {
      adSlot = googletag.defineOutOfPageSlot(fullGamSlug, this.id).setTargeting('slot_name', this.name);
    } else {
      adSlot = googletag.defineSlot(fullGamSlug, sizes, this.id).setTargeting('slot_name', this.name);
    }

    // Add service
    adSlot.addService(googletag.pubads());

    // Set the targeting position to the slot name.
    adSlot.setTargeting('position', this.name);

    // Only if we don't have a hold size do we want to collapse the empty div.
    // Note: We cannot do this on slots that we are going to watch— else they
    //       will never show up.
    if (this.getHoldSize() === null && !this.isWatcherEligible()) {
      adSlot.setCollapseEmptyDiv(true, true);
    }

    // Add custom slot targeting from config to targeting object
    if (this.data.targeting && typeof this.data.targeting === 'object') {
      Object.keys(this.data.targeting).forEach(key => {
        this.targeting[key] = this.data.targeting[key];
      });
    }

    // Adding Slot level variables for Pinnacle Sites
    // CBG To do: Change this to a config variable so it's more clear
    if (this.app.settings.slotVariables) {
      const adType = this.wrapper?.wrapperElement?.parentElement?.dataset?.adType
        ? this.wrapper?.wrapperElement?.parentElement?.dataset?.adType
        : '';

      const slotTargeting = {
        ...this.app.settings.slotVariables,
        pos_ps: this.configName,
        page_path: slugPath,
        adType,
      };
      this.targeting = { ...this.targeting, ...slotTargeting };
    }

    // Setting local slot targeting
    Object.keys(this.targeting).forEach(key => {
      Logger.log(this.name + ' custom targeting: ' + key + ' => ' + this.targeting[key]);
      adSlot.setTargeting(key, this.targeting[key]);
    });

    // Store the slot within our slot instance
    this.slot = adSlot;

    // Since we are now using disableInitialLoad(), this just sets up the slot
    // A network request is not made until we call refresh() inside the ad.js show method
    // Wrapping in DV Tag Ready Method
    if (typeof window.onDvtagReady === 'function') {
      window.onDvtagReady(() => {
        googletag.display(this.id);
      });
    } else {
      googletag.display(this.id);
    }

    Events.emit(EventTypes.slotPublishedToAdServer, { slot: adSlot, name: this.name });
  });
}

/**
 * A function that listens for an ad's enable/disabled automatic refresh
 * and changes it depending on the passed value
 */
function listenForRefreshEligibility() {
  // Bypass this if running a unit test, and no element exists
  if (!this.getElement()) {
    return;
  }

  this.listeningForRefreshEligibility = true;

  this.getElement().addEventListener('refreshEligibilityToggle', evt => {
    this._state.refreshEligible = evt.detail.hasOwnProperty('refresh') ? !!evt.detail.refresh : false;
    Logger.log(
      `[Refresh] The refresh eligibility event was fired for ${this.data.name} with state ${
        this._state.refreshEligible
      }`
    );
  });
}

function listenForRefreshImmediately() {
  if (!this.getElement()) {
    return;
  }

  this.listeningForRefreshImmediately = true;

  this.getElement().addEventListener('refreshImmediately', () => {
    this._state.refreshEligible = true;
    this.queueRefreshImmediately();
    Logger.log(`[Refresh] The refresh immediately event was received for ${this.data.name} `);
  });
}

/**
 * Queue up a refresh for this slot
 * @return {undefined}
 */
function queueRefresh(refreshRateOverride) {
  // this logic only applies if we can refresh the slot
  if (!this.isRefreshEligible() || this.isRefreshQueued()) {
    return false;
  }
  Logger.log(`[Refresh] Queueing refresh for slot ${this.name}`);
  let refreshRate = Number.isInteger(refreshRateOverride)
    ? refreshRateOverride
    : getRefreshRate({ settings: this.app.settings, slot: this });

  // optimizeAdRefresh: first interval = refreshRate − time since display, so first refresh at exactly display + refreshRate.
  // If we're already past that (e.g. queueing the next cycle after a deferred refresh), use full refreshRate.
  let delay = refreshRate;
  if (this.isOptimizeAdRefreshEnabled() && this._state.lastDisplayTime) {
    const elapsed = Date.now() - this._state.lastDisplayTime;
    const offsetDelay = Math.max(0, refreshRate - elapsed);
    delay = offsetDelay > 0 ? offsetDelay : refreshRate;
  }

  this._state.refreshQueued = true;
  this.refreshTimer = setTimeout(() => {
    // We can disable refeshing globally, and if we do we want to do that here.
    // Just to make sure we catch it if that changes between the timout and now, we capture that.
    if (this.app.settings.disableRefreshing) {
      Logger.log(`[Refresh] Refreshing disabled: slot ${this.name} has not been refreshed`);
    } else if (this.isRefreshEligible()) {
      if (this.isOptimizeAdRefreshEnabled()) {
        if (this._state.viewedSinceRender) {
          Logger.log(`[Refresh] Starting refresh process for ${this.name}.`);
          this.refresh();
        } else {
          this._state.pendingRefresh = true;
          this._state.refreshQueued = false;
        }
      } else {
        Logger.log(`[Refresh] Starting refresh process for ${this.name}.`);
        this.refresh();
      }
    }
  }, delay);
}

/**
 * Will this slot be added to a container that has a limit on how many slots
 * can be added to it?
 * @param  {Node} node   Previous element/neighbor
 * @return {Boolean}     True if the container is maxed out; false if not.
 */
function containerLimitMaxedOut(node) {
  if (!this.app.settings.containerLimits) {
    return false;
  }

  return Object.keys(this.app.settings.containerLimits).some(selector => {
    if (!this.dom.querySelectorAll(selector).length) {
      return false;
    }

    // If the selector contains the node where we're inserting the ad, check
    // to see if this selector already has the max number of ads
    if (this.dom.querySelector(selector).contains(node)) {
      return (
        this.dom.querySelector(selector).querySelectorAll('.m-ad').length >= this.app.settings.containerLimits[selector]
      );
    }

    return false;
  });
}

/**
 * Resolve the size list to request when a slot refreshes, honoring the optional
 * app.settings.refreshSizeBehavior flag (see REFRESH_SIZE_BEHAVIOR):
 *
 *  - ALL_SIZES ('all-sizes'): skip the eligible-size calculation entirely and always request the
 *    full configured size list (this.data.sizes). Never narrowed on refresh.
 *  - PREVENT_EMPTY ('prevent-empty'): run the normal eligible-size calculation, but ONLY when it
 *    yields an empty list (e.g. the previous render was empty / had no measurable iframe) retain the
 *    sizes requested in the previous cycle rather than requesting [], which would define a sizeless,
 *    unfillable slot. A measurable render still narrows normally.
 *  - unset / any other value: existing behavior — request only sizes within threshold of
 *    renderedSize.
 *
 * Called with the slot as `this`.
 *
 * @param {Array} previousRequestedSizes  Sizes requested in the previous cycle.
 * @returns {Array} Sizes to request on this refresh.
 */
function sizesForRefresh(previousRequestedSizes) {
  const behavior = this.app.settings.refreshSizeBehavior;

  // Always request the full configured size list; never reset/narrow on refresh.
  if (behavior === REFRESH_SIZE_BEHAVIOR.ALL_SIZES) {
    return this.sizes; // The super.applyDefaults reset this.sizes to configured sizes.
  }

  const eligibleSizes = eligibleSizesForRefresh(this.renderedSize, this.sizes);

  if (behavior === REFRESH_SIZE_BEHAVIOR.PREVENT_EMPTY) {
    // When the refresh would request nothing (empty ad / unmeasurable render), keep the sizes we
    // requested last time instead of requesting [] and defining a slot that can never fill.
    if (eligibleSizes.length === 0) {
      Logger.log(`[Refresh] eligible sizes is empty, fallback to previous request sizes`);

      // The last request is valid, so return last known sizes, should already include the special sizes.
      return previousRequestedSizes;
    }

    // Special sizes (e.g. `fluid`) have no comparable dimensions, so eligibleSizesForRefresh always
    // drops them. Re-add any the configured sizes include, so the refresh keeps requesting them
    // alongside the narrowed fixed sizes.
    // this.sizes resulted from super.applyDefaults which is the configured sizes.
    return preserveSpecialSizes(eligibleSizes, this.sizes);
  }

  return eligibleSizes;
}

/**
 * Re-add any special (non-fixed) sizes to a refresh size list when the source (configured) sizes
 * include them. Special sizes (see SPECIAL_SIZES — e.g. `fluid`) have no comparable dimensions, so
 * the threshold-based eligibleSizesForRefresh() always filters them out; under the prevent-empty
 * behavior we keep requesting them. No-op for special sizes the source lacks or the list already
 * contains.
 *
 * @param {Array} sizes        The resolved refresh sizes.
 * @param {Array} sourceSizes  The configured sizes to check for special sizes.
 * @returns {Array} sizes, with any applicable special sizes appended.
 */
function preserveSpecialSizes(sizes, sourceSizes) {
  if (!Array.isArray(sourceSizes)) {
    return sizes;
  }

  const preserved = [...sizes];

  SPECIAL_SIZES.forEach(special => {
    const sourceHasSpecial = sourceSizes.some(size => sizesEqual(size, special));
    const alreadyPresent = preserved.some(size => sizesEqual(size, special));

    if (sourceHasSpecial && !alreadyPresent) {
      preserved.push(special);
    }
  });

  return preserved;
}

/**
 * Value-equality for a slot size, which may be a [w, h] array or a special string such as `fluid`.
 * @param {Array|string} a
 * @param {Array|string} b
 * @returns {boolean}
 */
function sizesEqual(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((value, index) => value === b[index]);
  }

  return a === b;
}

/**
 * Reduce the default sizes to those that are within 5px of the rendered size
 * Created to account for custom sizes used in takeovers
 *
 * @param renderedSize: Array
 * @param defaultSizes: Array
 * @returns [number, number][] Sizes
 */
function eligibleSizesForRefresh(renderedSize, defaultSizes) {
  const threshold = 5;
  return defaultSizes.reduce((eligibleSizes, size) => {
    const widthInThreshold = Math.abs(size[0] - renderedSize[0]) <= threshold;
    const heightInThreshold = Math.abs(size[1] - renderedSize[1]) <= threshold;

    if (widthInThreshold && heightInThreshold) {
      Logger.log(`[Refresh] ${size.join('x')} eligible for refresh.`);
      eligibleSizes.push(size);
    }

    return eligibleSizes;
  }, []);
}

/**
 * Reduce the sizes array by any filters specifed on individual sizes.
 *
 * @param {{ sizes: any, name: string }} slotConfig
 * @param {Element} neighbor Neighboring element
 * @returns [number, number][] Sizes
 */
function reduceSizesByFilters({ sizes, name }, neighbor, settings) {
  return sizes.reduce((flattenedSizes, size) => {
    if (!size.size) {
      flattenedSizes.push(size);
      return flattenedSizes;
    }

    if (size.filters && !Slot.filterSlot({ config: size, el: neighbor, settings, scopedDom: document })) {
      Logger.log(`${name} had a slot size fail filters: ${size.size.join('x')}`);
    } else {
      flattenedSizes.push(size.size);
    }

    return flattenedSizes;
  }, []);
}

export function bucketedRenderLocation(location) {
  return getClosestBucketForValue({
    value: location,
    precision: 0.25,
    lowerBounds: -0.5,
    upperBounds: 1.75,
  });
}
